import React, { useEffect, useLayoutEffect, useMemo, useRef } from "react";
import dayjs from "dayjs";
import "dayjs/locale/nl";
import { formatCurrency } from "../utils/format";
import { fxSettings } from "../utils/renderTier";
import { gaugeGeometry } from "../utils/gaugeGeometry";
import { heatColor, rainbowColors } from "../utils/heatColor";
import { chargeCellMs, chargeCells } from "./charge";
import {
  maxRows,
  missedSalesMs,
  rowCount,
  rowDelayMs,
  totalAfterMs,
} from "./missedSales";
import {
  partyBlocksMs,
  partyCannonSpread,
  partyLightsMs,
  partyLineMs,
  partyLines,
  targetPartyMs,
} from "./party";
import {
  targetChargeMs,
  targetFlyMs,
  targetFullMs,
  targetSweepAt,
  targetSweepStartMs,
} from "./targetCharge";
import "./animations.css";

// De laag die over het dashboard heen ligt. Alles hier is pointer-events:none
// en verdwijnt vanzelf: de hook haalt een run uit de lijst zodra hij klaar is.
//
// Animaties die een bestaand onderdeel aansturen (meter, podium, rijen) staan
// hier niet - die lopen via een klasse op de dashboard-div, zie animations.css.
//
// Hoeveel deeltjes er vallen hangt af van de kwaliteitstrap, zie
// utils/renderTier.js. Op een trage Pi is het aantal elementen dat tegelijk
// beweegt het verschil tussen vloeiend en haperen, dus dat is het eerste wat
// we terugschroeven. De vorm van de animatie blijft gelijk: dezelfde tijden,
// dezelfde richting, alleen dunner bezaaid.

const colors = ["#ffffff", "#ffd166", "#ff9a3c", "#7fd8ff", "#cfeaff"];

// Vaste willekeur: één keer uitrekenen per run, anders springt alles zodra
// React opnieuw tekent.
const spread = (count, build) => Array.from({ length: count }, (_, i) => build(i));

// Evenredig uitdunnen: elke zoveelste uit de lijst in plaats van de eerste
// paar. Zo blijven de knallen over het hele beeld verdeeld en blijven ze na
// elkaar komen, ook als er maar een handvol overblijft.
const thin = (list, keep) => {
  if (keep >= list.length) return list;
  const step = list.length / keep;
  return spread(keep, (i) => list[Math.floor(i * step)]);
};

// De aanloop: het beeld gaat donker en een rij blokjes loopt vol, eerst
// traag en dan steeds sneller. Dit staat voor de aankondiging, zodat de tv
// eerst de aandacht pakt en je daarna pas leest wie er wat verkocht heeft.
// Als de rij vol is flitst het beeld kort, en de aankondiging begint al
// terwijl dit nog staat: zie chargeHandoff in hooks/useSaleEvents.js.
//
// Elk blokje is ook een tik die je hoort; de tijden komen daarom uit
// ./charge.js, waar het geluid ze ook vandaan haalt.
//
// Alleen opacity en transform, dus ook op de traagste Pi kost dit niets: het
// zijn zestien vlakjes die één keer getekend worden en daarna alleen nog
// aanspringen.
const chargeDelays = Array.from({ length: chargeCells }, (_, i) => chargeCellMs(i));

const SaleCharge = () => (
  <div className="fx-charge">
    <div className="fx-charge-dim" />
    <div className="fx-charge-card">
      <div className="fx-charge-kicker">Er komt iets binnen</div>
      <div className="fx-charge-cells">
        {chargeDelays.map((delay, i) => (
          <span key={i} className="fx-charge-cell">
            <i className="fx-charge-cell-on" style={{ animationDelay: `${delay}ms` }} />
          </span>
        ))}
      </div>
    </div>
    <div className="fx-charge-flash" />
  </div>
);

// De aankondiging van een verkoop: het beeld gaat donker en in het midden
// staat in goud wie er wat heeft verkocht. Hetzelfde idee als de recordkaart
// bij het vuurwerk, maar zonder het vuurwerk. Zodra dit weg is valt de balk
// bovenlangs, zie saleGroup in hooks/useSaleEvents.js. De regels komen één
// voor één binnen, van boven naar beneden.
const SaleSplash = ({ name, amount, service }) => (
  <div className="fx-splash">
    <div className="fx-splash-dim" />
    <div className="fx-splash-card">
      <div className="fx-splash-line fx-splash-kicker">Nieuwe verkoop van</div>
      <div className="fx-splash-line fx-splash-name">{name}</div>
      <div className="fx-splash-line fx-splash-amount">€{formatCurrency(amount)}</div>
      {service ? (
        <div className="fx-splash-line fx-splash-service">{service}</div>
      ) : null}
    </div>
  </div>
);

// Bij een verkoop vallen er meerdere buien over elkaar heen, en de kaart
// "Verkopen gemist" heeft een korter venster, dus tellen en tijden zijn
// instelbaar: houd maxDelay + minDuration + durationSpread onder de `ms` van
// confetti-burst in registry.js.
//
// count is een verhouding en geen vast aantal: 1 is een volle bui, en de
// regens hieronder vragen om een derde daarvan. Hoeveel snippers dat zijn
// bepaalt de kwaliteitstrap.
//
// after: zoveel seconden wachten voor de eerste snipper valt. De kaart
// "Verkopen gemist" laat hem pas los als de lijst compleet is.
const ConfettiBurst = ({
  count = 1,
  maxDelay = 1.6,
  minDuration = 2.6,
  durationSpread = 2,
  after = 0,
}) => {
  const bits = useMemo(
    () =>
      spread(Math.max(6, Math.round(fxSettings().confettiBits * count)), (i) => ({
        left: Math.random() * 100,
        delay: after + Math.random() * maxDelay,
        duration: minDuration + Math.random() * durationSpread,
        size: 8 + Math.random() * 10,
        round: i % 3 === 0,
        color: colors[i % colors.length],
      })),
    [count, maxDelay, minDuration, durationSpread, after],
  );

  return (
    <div className="fx-confetti">
      {bits.map((bit, i) => (
        <span
          key={i}
          className={`fx-confetti-bit${bit.round ? " round" : ""}`}
          style={{
            left: `${bit.left}%`,
            width: `${bit.size}px`,
            height: `${bit.size * (bit.round ? 1 : 0.45)}px`,
            background: bit.color,
            animationDelay: `${bit.delay}s`,
            animationDuration: `${bit.duration}s`,
          }}
        />
      ))}
    </div>
  );
};

const RankUp = ({ name, position, from }) => (
  <div className="fx-rankup">
    <div className="fx-rankup-arrow">▲</div>
    <div className="fx-rankup-text">
      <div className="fx-rankup-name">{name}</div>
      <div className="fx-rankup-move">
        {from ? `van #${from} naar ` : "nu "}
        <b>#{position}</b>
      </div>
    </div>
  </div>
);

// Vuurwerk hoort bij een gebroken record. Zonder tekst is het alleen maar
// beweging, dus de kaart vertelt wie het is, voor hoeveel, en waar het oude
// record stond. Het beeld gaat er donker achter, anders valt witte tekst weg
// tegen de lichte kaarten van het dashboard.
//
// De knallen staan langs de randen: het midden blijft vrij voor de tekst.
const burstSpots = [
  { x: 14, y: 20, color: "#ffd166", delay: 0 },
  { x: 84, y: 16, color: "#7fd8ff", delay: 0.15 },
  { x: 30, y: 44, color: "#ff9a3c", delay: 0.3 },
  { x: 70, y: 38, color: "#ffffff", delay: 0.45 },
  { x: 8, y: 62, color: "#cfeaff", delay: 0.6 },
  { x: 92, y: 56, color: "#ffd166", delay: 0.75 },
  { x: 46, y: 12, color: "#7fd8ff", delay: 0.95 },
  { x: 22, y: 82, color: "#ff9a3c", delay: 1.15 },
  { x: 78, y: 84, color: "#ffffff", delay: 1.35 },
  { x: 58, y: 66, color: "#ffd166", delay: 1.55 },
  { x: 36, y: 26, color: "#cfeaff", delay: 1.8 },
  { x: 66, y: 20, color: "#ff9a3c", delay: 2 },
  { x: 12, y: 40, color: "#ffffff", delay: 2.2 },
  { x: 88, y: 34, color: "#7fd8ff", delay: 2.4 },
  { x: 42, y: 86, color: "#ffd166", delay: 2.6 },
  { x: 62, y: 46, color: "#ff9a3c", delay: 2.85 },
  { x: 26, y: 62, color: "#7fd8ff", delay: 3.05 },
  { x: 74, y: 70, color: "#cfeaff", delay: 3.25 },
];

// De vonken zijn platte bolletjes zonder gloed eromheen. Een box-shadow met
// blur per vonk betekent honderden vlakken die de browser niet kan
// doorschuiven maar per frame opnieuw moet tekenen, en daar loopt een Pi op
// stuk. De kleuren zijn fel genoeg om het zonder die gloed te doen.
const Fireworks = ({ name, amount, service, previous }) => {
  const bursts = useMemo(() => {
    const fx = fxSettings();

    // Twee ringen per knal: een dichte kern en een wijdere buitenrand. Dat
    // leest van een afstand als één bol vonken in plaats van een kring losse
    // stippen. Op de laagste trap blijft alleen de buitenring over, want die
    // bepaalt de vorm.
    const rings = fx.burstRings > 1 ? [0.55, 1] : [1];

    return thin(burstSpots, fx.burstSpots).map((burst) => {
      const count = fx.burstBits + Math.floor(Math.random() * 8);
      const bits = [];
      rings.forEach((ring, r) => {
        spread(count, (i) => {
          const angle = ((i + r * 0.5) / count) * Math.PI * 2;
          const distance = (140 + Math.random() * 150) * ring;
          bits.push({
            tx: Math.cos(angle) * distance,
            ty: Math.sin(angle) * distance,
            size: (11 + Math.random() * 9) * (rings.length > 1 && !r ? 0.7 : 1),
          });
        });
      });
      return { ...burst, bits };
    });
  }, []);

  return (
    <div className="fx-fireworks">
      <div className="fx-fireworks-dim" />

      {bursts.map((burst, i) => (
        <div
          key={i}
          className="fx-burst"
          style={{ left: `${burst.x}%`, top: `${burst.y}%` }}
        >
          {burst.bits.map((bit, j) => (
            <span
              key={j}
              className="fx-burst-bit"
              style={{
                background: burst.color,
                animationDelay: `${burst.delay}s`,
                width: `${bit.size}px`,
                height: `${bit.size}px`,
                margin: `${-bit.size / 2}px 0 0 ${-bit.size / 2}px`,
                "--tx": `${bit.tx}px`,
                "--ty": `${bit.ty}px`,
              }}
            />
          ))}
        </div>
      ))}

      {name ? (
        <div className="fx-record-wrap">
          <div className="fx-record">
            <div className="fx-record-kicker">Nieuw record</div>
            <div className="fx-record-title">GROOTSTE VERKOOP OOIT</div>
            <div className="fx-record-name">{name}</div>
            <div className="fx-record-amount">€{formatCurrency(amount)}</div>
            {service ? <div className="fx-record-service">{service}</div> : null}
            {previous ? (
              <div className="fx-record-previous">
                oude record €{formatCurrency(previous)}
              </div>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
};

// Het eigen record: iemands grootste verkoop tot nu toe. Dezelfde opzet als
// de recordkaart bij het vuurwerk, en met opzet niet hetzelfde beeld: dit
// gaat over één persoon die zichzelf verbetert, niet over het hele bord.
// Daarom geen vonken maar strepen die vanuit het midden opzij schuiven -
// twaalf platte balkjes, dus ook op de traagste Pi kost dit niets.
//
// De strepen liggen boven en onder de kaart, zodat het midden vrij blijft
// voor de tekst. De kaart is ongeveer 390px hoog en staat gecentreerd op een
// vlak van 1080, dus alles tussen 31% en 69% is bezet; daar blijven deze
// vanaf. Vaste posities en geen willekeur: er hoeft hier niets te fonkelen.
const bestRays = [
  { top: 13, width: 240, delay: 0.1 },
  { top: 19, width: 150, delay: 0.22 },
  { top: 26, width: 320, delay: 0.16 },
  { top: 74, width: 300, delay: 0.26 },
  { top: 81, width: 170, delay: 0.34 },
  { top: 87, width: 250, delay: 0.2 },
];

const PersonalBest = ({ name, amount, service, previous }) => (
  <div className="fx-best">
    <div className="fx-best-dim" />

    {bestRays.map((ray, i) =>
      [-1, 1].map((side) => (
        <span
          key={`${i}-${side}`}
          className="fx-best-ray"
          style={{
            top: `${ray.top}%`,
            width: `${ray.width}px`,
            [side < 0 ? "right" : "left"]: "50%",
            // Groeien naar buiten toe, dus het vaste punt is de kant die aan
            // het midden vast zit.
            transformOrigin: side < 0 ? "right center" : "left center",
            animationDelay: `${ray.delay}s`,
          }}
        />
      )),
    )}

    <div className="fx-best-wrap">
      <div className="fx-best-card">
        <div className="fx-best-line fx-best-kicker">Eigen record</div>
        <div className="fx-best-line fx-best-name">{name}</div>
        <div className="fx-best-line fx-best-sub">grootste verkoop tot nu toe</div>
        <div className="fx-best-line fx-best-amount">€{formatCurrency(amount)}</div>
        {service ? (
          <div className="fx-best-line fx-best-service">{service}</div>
        ) : null}
        {previous ? (
          <div className="fx-best-line fx-best-previous">
            was €{formatCurrency(previous)}
          </div>
        ) : null}
      </div>
    </div>
  </div>
);

// De aanloop naar het target: het beeld wordt donker, de meter uit de hero
// stijgt op en groeit naar het midden, en daar loopt de wijzer van 0 naar
// 100. Op 100 licht het beeld kort op, veert de meter, en wordt het donker
// dieper; dan valt "GEHAALD" eroverheen (zie targetPartyGroup in party.js).
//
// De vlucht is een FLIP: de meter staat meteen op zijn eindplek in het
// midden, en we meten hoe ver en hoeveel kleiner de meter in de hero is. Daar
// begint de animatie, en ze loopt terug naar niets: één transform, dus de
// videokaart doet het werk. De hero-meter zelf is zolang onzichtbaar (zie
// .anim-target-charge in animations.css), zodat het leest als dezelfde meter
// die opstijgt. Is er geen hero te vinden, dan groeit hij uit het midden.
//
// De wijzer, de boog en het getal lopen per frame mee, en dat is het enige op
// het dashboard wat dat doet. Het kan niet anders: een getal dat optelt kan
// CSS niet. Maar het zijn drie waarden in de DOM, direct gezet via refs,
// zonder dat React opnieuw tekent. Al het andere (het donker, het opspringen,
// het goud) staat in de CSS en krijgt de momenten als variabelen mee.
//
// Plat, zoals de meter in de hero op de laagste trap: geen gloed, geen
// filter. De kleur van de boog loopt wel mee met de hitte, van amber naar
// rood; dat is de huisstijl van de meter.
const tcSize = 760;
const tcStroke = 44;

const TargetCharge = () => {
  const geometry = useMemo(() => gaugeGeometry(tcSize, tcStroke, 0), []);
  const flyRef = useRef(null);
  const arcRef = useRef(null);
  const needleRef = useRef(null);
  const numberRef = useRef(null);

  // Voor het eerste beeld: waar staat de meter in de hero? Het vlak is
  // geschaald (ScaledCanvas), dus de schermmaten gaan terug naar de 1920x1080
  // waar de CSS in rekent.
  useLayoutEffect(() => {
    const fly = flyRef.current;
    const layer = fly?.closest(".fx-layer");
    const hero = fly
      ?.closest(".dashboard-container")
      ?.querySelector(".hero-gauge svg");
    if (!fly || !layer || !hero || !layer.offsetWidth) return;

    const scale = layer.getBoundingClientRect().width / layer.offsetWidth;
    const from = hero.getBoundingClientRect();
    // Zonder de vlucht meten: die staat op dit moment al op zijn beginstand,
    // en getBoundingClientRect telt die transform mee. Terugzetten start de
    // animatie opnieuw, maar we staan nog op het eerste beeld.
    fly.style.animation = "none";
    const to = fly.getBoundingClientRect();
    fly.style.animation = "";
    if (!from.width || !to.width) return;

    const dx = (from.left + from.width / 2 - (to.left + to.width / 2)) / scale;
    const dy = (from.top + from.height / 2 - (to.top + to.height / 2)) / scale;
    fly.style.setProperty("--fly-x", `${dx}px`);
    fly.style.setProperty("--fly-y", `${dy}px`);
    fly.style.setProperty("--fly-s", `${from.width / to.width}`);
  }, []);

  useEffect(() => {
    const begin = performance.now();
    let frame = 0;

    const draw = (now) => {
      const part = targetSweepAt(now - begin);
      const arc = arcRef.current;
      if (arc) {
        arc.style.strokeDashoffset = `${geometry.length * (1 - part)}`;
        arc.style.stroke = heatColor(part * 100);
        // Een boog van lengte nul tekent met zijn ronde uiteinde nog een stip.
        arc.style.opacity = part > 0.004 ? "1" : "0";
      }
      if (needleRef.current) {
        needleRef.current.style.transform = `rotate(${180 * part}deg)`;
      }
      if (numberRef.current) {
        numberRef.current.textContent = `${Math.round(part * 100)}%`;
      }
      // Tot de 100 en niet tot de stand 1: hij vertrekt ook op 1.
      if (now - begin < targetFullMs) {
        frame = requestAnimationFrame(draw);
      } else if (arc) {
        // Op 100 de regenboog, net als de meter in de hero straks: dezelfde
        // meter, en hij staat vol. Tegelijk met de flits, dus je ziet de
        // kleur niet verspringen maar opengaan.
        arc.style.stroke = "url(#fxTcRainbow)";
      }
    };

    frame = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(frame);
  }, [geometry]);

  const { cx, cy, radius, height, path, length, ticks } = geometry;

  return (
    <div
      className="fx-tc"
      style={{
        "--fx-tc-start": `${targetSweepStartMs}ms`,
        "--fx-tc-full": `${targetFullMs}ms`,
        "--fx-tc-fly": `${targetFlyMs}ms`,
        "--fx-tc-ms": `${targetChargeMs}ms`,
      }}
    >
      <div className="fx-tc-dim" />
      <div className="fx-tc-card">
        <div className="fx-tc-pop">
          <div className="fx-tc-kicker">Team target</div>
          <div ref={flyRef} className="fx-tc-fly">
            <svg
              className="fx-tc-gauge"
              width={tcSize}
              height={height}
              viewBox={`0 0 ${tcSize} ${height}`}
            >
              <defs>
                <linearGradient id="fxTcRainbow" x1="0" y1="0" x2="1" y2="0">
                  {rainbowColors.map((color, index) => (
                    <stop
                      key={color}
                      offset={index / (rainbowColors.length - 1)}
                      stopColor={color}
                    />
                  ))}
                </linearGradient>
              </defs>
              {ticks.map((tick, i) => (
                <line
                  key={i}
                  x1={tick.x1}
                  y1={tick.y1}
                  x2={tick.x2}
                  y2={tick.y2}
                  stroke={tick.major ? "rgba(255, 255, 255, 0.55)" : "rgba(255, 255, 255, 0.25)"}
                  strokeWidth={tick.major ? 4 : 2}
                  strokeLinecap="round"
                />
              ))}
              <path
                d={path}
                fill="none"
                stroke="rgba(255, 255, 255, 0.12)"
                strokeWidth={tcStroke}
                strokeLinecap="round"
              />
              <path
                ref={arcRef}
                d={path}
                fill="none"
                stroke={heatColor(100)}
                strokeWidth={tcStroke}
                strokeLinecap="round"
                style={{ strokeDasharray: `${length} ${length}`, strokeDashoffset: 0 }}
              />
              <g
                ref={needleRef}
                style={{
                  transformBox: "view-box",
                  transformOrigin: `${cx}px ${cy}px`,
                  transform: "rotate(180deg)",
                }}
              >
                <line
                  x1={cx}
                  y1={cy}
                  x2={cx - radius + tcStroke}
                  y2={cy}
                  stroke="#ffffff"
                  strokeWidth={10}
                  strokeLinecap="round"
                />
              </g>
              <circle cx={cx} cy={cy} r={22} fill="#ffffff" />
            </svg>
          </div>
          <div ref={numberRef} className="fx-tc-number">
            100%
          </div>
        </div>
      </div>
      <div className="fx-tc-flash" />
    </div>
  );
};

const TargetHit = ({ amount }) => (
  <div className="fx-target">
    <ConfettiBurst />
    <div className="fx-target-card">
      <div className="fx-target-kicker">Team target</div>
      <div className="fx-target-title">GEHAALD</div>
      {amount ? (
        <div className="fx-target-amount">€{formatCurrency(amount)}</div>
      ) : null}
    </div>
  </div>
);

// Het beeld een tikje donkerder zolang het targetfeest duurt, zodat de
// lichtshow en de confetti ervan loskomen. Zie targetPartyGroup in party.js.
const TargetPartyDim = () => (
  <div className="fx-target-dim" style={{ "--fx-target-dim-ms": `${targetPartyMs}ms` }} />
);

const ScreenGlow = () => <div className="fx-glow" />;

// "sinds gisteren 17:42": hoe laat het scherm uitging, zoals je het zou
// zeggen. Vandaag en gisteren bij naam, daarvoor de dag zelf.
function sinceLabel(since) {
  if (!since) return "";
  const then = dayjs(since).locale("nl");
  const today = dayjs();
  const time = then.format("HH:mm");
  if (then.isSame(today, "day")) return `vandaag ${time}`;
  if (then.isSame(today.subtract(1, "day"), "day")) return `gisteren ${time}`;
  return `${then.format("dddd D MMMM")} ${time}`;
}

// En hoe lang het scherm daarna donker heeft gestaan: "14 uur", "40
// minuten". Grof afgerond, want het is een sfeerregel en geen meting.
function darkLabel(since) {
  if (!since) return "";

  const minutes = Math.max(1, Math.round((Date.now() - since) / 60000));
  if (minutes < 60) return `${minutes} minuten`;

  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} uur`;

  const days = Math.round(hours / 24);
  return days === 1 ? "een dag" : `${days} dagen`;
}

// De regels op de kaart: de verkopen zoals ze binnenkwamen, grootste eerst.
// Passen ze niet allemaal, dan worden de kleinste samen één laatste regel,
// zodat het totaal onderaan nog steeds klopt met wat er staat.
function missedRows(sales) {
  const rows = rowCount(sales.length);
  if (sales.length <= maxRows) return sales;

  const shown = sales.slice(0, rows - 1);
  const rest = sales.slice(rows - 1);
  return [
    ...shown,
    {
      name: `en nog ${rest.length} verkopen`,
      amount: rest.reduce((sum, sale) => sum + sale.amount, 0),
      service: null,
      rest: true,
    },
  ];
}

// Na het aanzetten van de tv: het scherm stond uit, en dit is wat er
// ondertussen is verkocht. Eerst de kop, dan de verkopen één voor één (wie,
// wat, hoeveel), en als de lijst compleet is het totaal en confetti. Hoe
// lang de kaart staat hangt af van het aantal regels, zie missedSales.js;
// de duur en de vertraging per regel gaan als inline stijl naar de CSS.
//
// Is er niets verkocht, dan komt de kaart alsnog: hij zegt dan wanneer er
// voor het laatst iemand meekeek en hoe lang het daarna donker was. Dat is
// ook het teken dat de tv weer leeft, en het is een stuk vriendelijker dan
// een scherm dat doet alsof er niets gebeurd is.
//
// Plat en zonder gloed, want dit speelt op het moment dat de Pi net opstart
// en het minst te geven heeft.
const MissedSales = ({ since, amount, sales = [] }) => {
  const rows = missedRows(sales);
  const empty = rows.length === 0;
  const lastRowMs = rows.length ? rowDelayMs(rows.length - 1) : 0;
  const totalMs = lastRowMs + totalAfterMs;

  return (
    <div
      className={`fx-missed${empty ? " is-empty" : ""}`}
      style={{ "--fx-missed-ms": `${missedSalesMs(sales.length)}ms` }}
    >
      {!empty && <ConfettiBurst after={totalMs / 1000} maxDelay={1.2} />}

      <div className="fx-missed-card">
        <div className="fx-missed-line fx-missed-kicker">
          {empty ? "Welkom terug" : "Even inhalen"}
        </div>
        <div className="fx-missed-line fx-missed-title">
          {empty ? "NIKS GEMIST" : "VERKOPEN GEMIST"}
        </div>
        <div className="fx-missed-line fx-missed-since">
          Laatste keer bekeken: {sinceLabel(since)}
          <span className="fx-missed-dot">·</span>
          {darkLabel(since)} donker
        </div>

        {empty && (
          <div
            className="fx-missed-line fx-missed-empty"
            style={{ animationDelay: `${rowDelayMs(0)}ms` }}
          >
            <div className="fx-missed-empty-text">
              Helaas, er is niks verkocht
            </div>
            <div className="fx-missed-empty-note">Op naar de eerste van vandaag</div>
          </div>
        )}

        <ol className="fx-missed-list">
          {rows.map((sale, index) => (
            <li
              key={`${sale.name}-${index}`}
              className={`fx-missed-line fx-missed-row${sale.rest ? " is-rest" : ""}`}
              style={{ animationDelay: `${rowDelayMs(index)}ms` }}
            >
              <span className="fx-missed-row-index">{sale.rest ? "+" : index + 1}</span>
              <span className="fx-missed-row-who">
                <span className="fx-missed-row-name">{sale.name}</span>
                {sale.service && (
                  <span className="fx-missed-row-service">{sale.service}</span>
                )}
              </span>
              <span className="fx-missed-row-amount">€{formatCurrency(sale.amount)}</span>
            </li>
          ))}
        </ol>

        {!empty && (
          <div
            className="fx-missed-line fx-missed-total"
            style={{ animationDelay: `${totalMs}ms` }}
          >
            Samen <b>€{formatCurrency(amount)}</b> erbij
          </div>
        )}
      </div>
    </div>
  );
};

// Het feestje, deel twee: banen kleur die schuin over het beeld schuiven.
//
// Ze zitten samen in één strook, en die strook doet het werk. Hij is twee
// schermen breed met twee keer hetzelfde patroon erin, en schuift er precies
// één op: daarna staat hij weer zoals hij begon, dus hij kan naadloos
// opnieuw zonder dat er iets terugspringt. Er beweegt dus één element in
// plaats van een stuk of twintig, en dat is op de trage Pi het verschil.
//
// Vlak en doorzichtig: het dashboard moet er gewoon doorheen te lezen
// blijven. Dit is een feestje over de cijfers heen, geen scherm vol kleur.
const bandColors = ["#ffd166", "#ff9a3c", "#7fd8ff", "#cfeaff", "#ffffff"];

const PartyBlocks = () => {
  const { partyBands } = fxSettings();
  const bands = spread(partyBands * 2, (i) => ({
    // De strook is twee schermen breed, dus één schermbreedte is 50% en
    // staan er partyBands banen in: de helft daarvan per stap.
    left: `${(i * 50) / partyBands}%`,
    color: bandColors[i % bandColors.length],
  }));

  return (
    <div
      className="fx-party-blocks"
      style={{ "--fx-party-blocks-ms": `${partyBlocksMs}ms` }}
    >
      <div className="fx-party-strip">
        {bands.map((band, i) => (
          <i key={i} style={{ left: band.left, background: band.color }} />
        ))}
      </div>
    </div>
  );
};

// En deel drie: de balk onderin die aanmoedigingen doorbladert. Eén regel per
// beurt, de volgende schuift eroverheen, en als ze op zijn zakt de balk weer
// weg. De regels staan in party.js, want daar staat ook hoe lang er één in
// beeld blijft en dat is hetzelfde getal.
//
// Onderin en niet in het midden: de cijfers zijn waar het dashboard voor is,
// en die blijven ook tijdens een feestje gewoon te lezen.
// Het target geeft zijn eigen regels mee (zie targetPartyGroup in party.js);
// zonder regels zijn het die van het gewone feestje.
const PartyHype = ({ lines = partyLines }) => (
  <div
    className="fx-party-hype"
    style={{ "--fx-party-hype-ms": `${lines.length * partyLineMs}ms` }}
  >
    <div className="fx-party-hype-bar">
      {lines.map((line, index) => (
        <span
          key={line}
          className="fx-party-hype-line"
          style={{
            animationDelay: `${index * partyLineMs}ms`,
            animationDuration: `${partyLineMs}ms`,
          }}
        >
          {line}
        </span>
      ))}
    </div>
  </div>
);

// De lichtshow: bundels die van boven het beeld in schijnen en heen en weer
// zwaaien, en op elke tel even oplichten.
//
// Een bundel is een vlak met een vaste kegelvorm (clip-path) dat alleen maar
// draait om het punt bovenaan. De vorm wordt één keer uitgerekend en daarna
// alleen nog gedraaid, en draaien doet de videokaart. Vlakke kleur met wat
// doorzichtigheid, geen verloop en geen blur: een podiumlamp, maar dan zoals
// de rest van het dashboard hem zou tekenen.
//
// De bundels zwaaien om en om de andere kant op, dan kruisen ze elkaar in het
// midden, en elke bundel heeft een eigen tempo zodat het niet gaat lezen als
// één ding dat heen en weer gaat.
const beamColors = ["#ffd166", "#7fd8ff", "#ff9a3c", "#cfeaff", "#ffd166", "#7fd8ff"];

const PartyLights = ({ ms = partyLightsMs }) => {
  const { partyBeams } = fxSettings();
  const beams = spread(partyBeams, (i) => ({
    // Verdeeld over de breedte, met aan de randen wat ruimte: een bundel die
    // precies in de hoek hangt schijnt voor de helft naast het scherm.
    left: `${8 + (i * 84) / Math.max(1, partyBeams - 1)}%`,
    color: beamColors[i % beamColors.length],
    reverse: i % 2 === 1,
    // Vier, vijf of zes tellen heen: net genoeg verschil.
    beats: 4 + (i % 3),
  }));

  return (
    <div
      className="fx-party-lights"
      style={{ "--fx-party-lights-ms": `${ms}ms` }}
    >
      {beams.map((beam, i) => (
        <i
          key={i}
          className={`fx-party-beam${beam.reverse ? " is-reverse" : ""}`}
          style={{
            left: beam.left,
            background: beam.color,
            "--fx-beam-beats": beam.beats,
          }}
        />
      ))}
    </div>
  );
};

// De kanonnen: confetti die vanuit beide hoeken onderin schuin omhoog wordt
// geschoten, naar het midden toe, en daarna weer naar beneden valt.
//
// Een boog uit één animatie halen gaat niet mooi: opzij hoort het af te
// remmen (luchtweerstand), omhoog hoort het af te remmen en daarna te
// versnellen (zwaartekracht). Dus elke snipper is twee elementen: de
// buitenste schuift opzij, de binnenste gaat op en neer en draait. Elk heeft
// zijn eigen tijdverloop, en samen maken ze de boog. Hoe ver en hoe hoog
// staat per snipper in een css-variabele, de vorm van de boog in de css.
const PartyCannons = () => {
  const bits = useMemo(() => {
    const perSide = fxSettings().partyCannonBits;
    return ["left", "right"].flatMap((side) =>
      spread(perSide, (i) => ({
        side,
        // Opzij en omhoog in px. Samen bepalen ze de hoek: van steil omhoog
        // langs de rand tot plat naar het midden toe.
        dx: (side === "left" ? 1 : -1) * (260 + Math.random() * 820),
        up: -(460 + Math.random() * 480),
        // Hoe ver hij daarna weer zakt, vanaf het hoogste punt.
        drop: 420 + Math.random() * 520,
        spin: (Math.random() * 2 - 1) * 900,
        delay: Math.random() * partyCannonSpread,
        duration: 2.2 + Math.random(),
        size: 9 + Math.random() * 10,
        round: i % 3 === 0,
        color: colors[i % colors.length],
      })),
    );
  }, []);

  return (
    <div className="fx-party-cannons">
      {bits.map((bit, i) => (
        <span
          key={i}
          className={`fx-cannon-bit is-${bit.side}`}
          style={{
            "--dx": `${bit.dx}px`,
            "--up": `${bit.up}px`,
            "--drop": `${bit.drop}px`,
            "--spin": `${bit.spin}deg`,
            animationDelay: `${bit.delay}s`,
            animationDuration: `${bit.duration}s`,
          }}
        >
          <i
            className={bit.round ? "round" : ""}
            style={{
              width: `${bit.size}px`,
              height: `${bit.size * (bit.round ? 1 : 0.45)}px`,
              background: bit.color,
              animationDelay: `${bit.delay}s`,
              animationDuration: `${bit.duration}s`,
            }}
          />
        </span>
      ))}
    </div>
  );
};

// De stempel: als een verkoop ook een record is, slaat dit dwars over de
// aankondiging heen, nadat je hebt kunnen lezen wie wat verkocht. Eerst het
// nieuws, dan de verrassing; daarna volgt de recordkaart met het oude record
// erbij (zie saleGroup in hooks/useSaleEvents.js).
//
// Plat, als een inktstempel: een goud kader met goud erin, op een donker vlak
// zodat de regels eronder de tekst niet onleesbaar maken. De klap is een
// schaal van groot naar iets te klein en terug, met een kort vlak oplichten
// op het moment dat hij neerkomt.
const RecordStamp = ({ kind = "best" }) => (
  <div className="fx-stamp">
    <div className="fx-stamp-flash" />
    <div className="fx-stamp-mark">
      <div className="fx-stamp-kicker">
        {kind === "record" ? "Grootste verkoop ooit" : "Grootste verkoop tot nu toe"}
      </div>
      <div className="fx-stamp-text">
        {kind === "record" ? "NIEUW RECORD!" : "EIGEN RECORD!"}
      </div>
    </div>
  </div>
);

const effects = {
  "sale-charge": SaleCharge,
  "sale-splash": SaleSplash,
  "confetti-burst": ConfettiBurst,
  "record-stamp": RecordStamp,
  "rank-up": RankUp,
  "screen-glow": ScreenGlow,
  fireworks: Fireworks,
  "personal-best": PersonalBest,
  "target-charge": TargetCharge,
  "target-hit": TargetHit,
  "target-party-dim": TargetPartyDim,
  "missed-sales": MissedSales,
  // Dezelfde kaart met een lege lijst. Live komt dat geval ook uit
  // "missed-sales"; dit is er zodat je hem los kunt bekijken zonder een
  // ochtend zonder verkopen af te wachten. Zie registry.js.
  "missed-nothing": MissedSales,
  "party-blocks": PartyBlocks,
  "party-hype": PartyHype,
  "party-lights": PartyLights,
  "party-cannons": PartyCannons,
  // party-dance staat hier niet: dat beweegt de kaarten zelf en loopt dus
  // via de klasse op de dashboard-div, net als de meter en het podium.
};

const AnimationLayer = ({ active }) => (
  <div className="fx-layer" aria-hidden="true">
    {active.map((run) => {
      const Effect = effects[run.id];
      return Effect ? <Effect key={run.runId} {...run.payload} /> : null;
    })}
  </div>
);

export default AnimationLayer;
